import type {
  BookingPolicy,
  CostLevel,
  DimensionKey,
  FilterState,
  MobilityLimit,
  NeighbourhoodCharacter,
  Season,
  TimeOfDay,
  TransportMode,
  TravellerType,
  WaterRelationship,
} from "./types.js";

export interface DimensionDefinition {
  key: DimensionKey;
  label: string;
  shortLabel: string;
  description: string;
  group: "core" | "advanced";
}

export interface TravellerDefinition {
  value: TravellerType;
  label: string;
  featured: boolean;
}

export interface ChoiceDefinition<T extends string> {
  value: T;
  label: string;
}

export interface PresetDefinition {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  accent: "sun" | "rain" | "walk" | "family" | "budget" | "water";
  apply: () => FilterState;
}

export const dimensionDefinitions: DimensionDefinition[] = [
  {
    key: "sunny",
    label: "Sonnenschein",
    shortLabel: "Sonne",
    description: "Wie stark gewinnt das Erlebnis bei trockenem, freundlichem Wetter und blauem Himmel?",
    group: "core",
  },
  {
    key: "rain",
    label: "Regentauglichkeit",
    shortLabel: "Regen",
    description: "Wie gut funktioniert das Erlebnis bei nassem Wetter?",
    group: "core",
  },
  {
    key: "history",
    label: "Geschichte",
    shortLabel: "Geschichte",
    description: "Wie stark ist der historische Charakter?",
    group: "core",
  },
  {
    key: "atmosphere",
    label: "Atmosphäre",
    shortLabel: "Atmosphäre",
    description: "Wie besonders und stimmungsvoll fühlt sich der Ort an?",
    group: "core",
  },
  {
    key: "walking",
    label: "Zu Fuß entdecken",
    shortLabel: "Spaziergang",
    description: "Wie gut eignet sich das Erlebnis zum Erkunden zu Fuß?",
    group: "core",
  },
  {
    key: "water",
    label: "Wassererlebnis",
    shortLabel: "Wasser",
    description: "Wie präsent sind Inseln, Hafen, Ufer oder Wasserblicke?",
    group: "core",
  },
  {
    key: "localness",
    label: "Lokales Gefühl",
    shortLabel: "Lokal",
    description: "Wie nah fühlt sich das Erlebnis am Stockholmer Alltag an?",
    group: "core",
  },
  {
    key: "food",
    label: "Essen & Fika",
    shortLabel: "Essen",
    description: "Wie gut lässt sich das Erlebnis mit Essen oder Fika verbinden?",
    group: "core",
  },
  {
    key: "photography",
    label: "Fotomotive",
    shortLabel: "Fotos",
    description: "Wie stark sind Architektur, Motive und Aussichtspunkte?",
    group: "core",
  },
  {
    key: "children",
    label: "Mit Kindern",
    shortLabel: "Kinder",
    description: "Wie empfehlenswert ist das Erlebnis für Familien?",
    group: "core",
  },
  {
    key: "value",
    label: "Preis-Leistung",
    shortLabel: "Wert",
    description: "Wie überzeugend ist das Erlebnis gemessen an Preis, Zeit und Aufwand?",
    group: "core",
  },
  {
    key: "spontaneity",
    label: "Spontanität",
    shortLabel: "Spontan",
    description: "Wie einfach kann man sich kurzfristig dafür entscheiden?",
    group: "core",
  },
  {
    key: "experienceDensity",
    label: "Viel Erlebnis pro Stunde",
    shortLabel: "Dichte",
    description: "Wie viel relevantes Stockholm erlebt man in kurzer Zeit?",
    group: "core",
  },
  {
    key: "cold",
    label: "Kälte-Tauglichkeit",
    shortLabel: "Kälte",
    description: "Wie gut funktioniert das Erlebnis bei niedrigen Temperaturen?",
    group: "advanced",
  },
  {
    key: "culture",
    label: "Kultur",
    shortLabel: "Kultur",
    description: "Wie stark ist der kulturelle Inhalt?",
    group: "advanced",
  },
  {
    key: "nature",
    label: "Natur",
    shortLabel: "Natur",
    description: "Wie wichtig sind Grün, Landschaft und Naturerlebnis?",
    group: "advanced",
  },
  {
    key: "romance",
    label: "Romantik",
    shortLabel: "Romantik",
    description: "Wie gut passt das Erlebnis zu einem stimmungsvollen Tag zu zweit?",
    group: "advanced",
  },
  {
    key: "architecture",
    label: "Architektur",
    shortLabel: "Architektur",
    description: "Wie stark ist das Erlebnis für Architektur- und Designinteresse?",
    group: "advanced",
  },
  {
    key: "accessibility",
    label: "Zugänglichkeit",
    shortLabel: "Zugänglich",
    description: "Wie gut ist das Erlebnis bei eingeschränkter Mobilität nutzbar?",
    group: "advanced",
  },
  {
    key: "timeEfficiency",
    label: "Zeiteffizienz",
    shortLabel: "Effizient",
    description: "Wie gut ist das Verhältnis aus Reisezeit, Dauer und Erlebniswert?",
    group: "advanced",
  },
];

export const coreDimensionDefinitions = dimensionDefinitions.filter((definition) => definition.group === "core");
export const advancedDimensionDefinitions = dimensionDefinitions.filter((definition) => definition.group === "advanced");

export const travellerDefinitions: TravellerDefinition[] = [
  { value: "first_time", label: "Zum ersten Mal", featured: true },
  { value: "repeat_visitor", label: "Wiederholungsbesuch", featured: true },
  { value: "family", label: "Familie", featured: true },
  { value: "couple", label: "Paar", featured: true },
  { value: "solo", label: "Allein", featured: true },
  { value: "friends", label: "Freunde", featured: false },
  { value: "senior", label: "Senioren", featured: false },
  { value: "history", label: "Geschichtsinteressiert", featured: true },
  { value: "culture", label: "Kulturinteressiert", featured: true },
  { value: "photography", label: "Fotografie", featured: true },
  { value: "foodie", label: "Genuss & Fika", featured: true },
  { value: "nature", label: "Natur", featured: true },
  { value: "design", label: "Design & Architektur", featured: true },
  { value: "budget", label: "Kleines Budget", featured: true },
  { value: "luxury", label: "Premium", featured: false },
  { value: "nightlife", label: "Abends & Nachtleben", featured: false },
  { value: "wellness", label: "Entschleunigung", featured: false },
  { value: "adventure", label: "Abenteuer", featured: false },
];

export const costLevelLabels: Record<CostLevel | "any", string> = {
  any: "Alle Preisniveaus",
  free: "Nur kostenlos",
  budget: "Bis günstig",
  moderate: "Bis mittel",
  expensive: "Bis höherpreisig",
  premium: "Auch Premium",
};

export const priceOptions = [
  { value: "", label: "Kein Preislimit" },
  { value: "100", label: "Maximal 100 SEK" },
  { value: "200", label: "Maximal 200 SEK" },
  { value: "300", label: "Maximal 300 SEK" },
  { value: "500", label: "Maximal 500 SEK" },
];

export const durationOptions = [
  { value: "", label: "Beliebige Dauer" },
  { value: "60", label: "Maximal 1 Stunde" },
  { value: "90", label: "Maximal 1½ Stunden" },
  { value: "120", label: "Maximal 2 Stunden" },
  { value: "180", label: "Maximal 3 Stunden" },
  { value: "240", label: "Maximal 4 Stunden" },
  { value: "360", label: "Maximal 6 Stunden" },
];

export const travelTimeOptions = [
  { value: "", label: "Beliebige Anfahrt" },
  { value: "15", label: "Maximal 15 Minuten" },
  { value: "30", label: "Maximal 30 Minuten" },
  { value: "45", label: "Maximal 45 Minuten" },
  { value: "60", label: "Maximal 60 Minuten" },
];

export const bookingPolicyOptions: ChoiceDefinition<BookingPolicy>[] = [
  { value: "any", label: "Buchung ist egal" },
  { value: "not_essential", label: "Keine zwingende Buchung" },
  { value: "no_booking", label: "Nur ohne Vorbuchung" },
];

export const mobilityLimitOptions: ChoiceDefinition<MobilityLimit>[] = [
  { value: "any", label: "Alle Mobilitätsstufen" },
  { value: "easy", label: "Nur leicht zugänglich" },
  { value: "moderate", label: "Leicht bis moderat" },
  { value: "challenging", label: "Bis anspruchsvoll" },
];

export const seasonDefinitions: ChoiceDefinition<Season>[] = [
  { value: "winter", label: "Winter" },
  { value: "spring", label: "Frühling" },
  { value: "summer", label: "Sommer" },
  { value: "autumn", label: "Herbst" },
];

export const timeOfDayDefinitions: ChoiceDefinition<TimeOfDay>[] = [
  { value: "early_morning", label: "Früh morgens" },
  { value: "morning", label: "Vormittags" },
  { value: "lunch", label: "Mittags" },
  { value: "afternoon", label: "Nachmittags" },
  { value: "evening", label: "Abends" },
  { value: "night", label: "Spät" },
];

export const transportModeDefinitions: ChoiceDefinition<TransportMode>[] = [
  { value: "walking", label: "Zu Fuß" },
  { value: "metro", label: "U-Bahn" },
  { value: "bus", label: "Bus" },
  { value: "tram", label: "Straßenbahn" },
  { value: "ferry", label: "Fähre" },
  { value: "train", label: "Zug" },
  { value: "bike", label: "Fahrrad" },
  { value: "taxi", label: "Taxi" },
  { value: "car", label: "Auto" },
];

export const neighbourhoodCharacterDefinitions: ChoiceDefinition<NeighbourhoodCharacter>[] = [
  { value: "historic", label: "Historisch" },
  { value: "creative", label: "Kreativ" },
  { value: "residential", label: "Wohnviertel" },
  { value: "touristy", label: "Touristisch" },
  { value: "local", label: "Lokal" },
  { value: "upmarket", label: "Gehoben" },
  { value: "alternative", label: "Alternativ" },
  { value: "lively", label: "Lebendig" },
  { value: "quiet", label: "Ruhig" },
  { value: "trendy", label: "Trendbewusst" },
  { value: "traditional", label: "Traditionell" },
];

export const waterRelationshipDefinitions: ChoiceDefinition<WaterRelationship>[] = [
  { value: "waterfront", label: "Ufer" },
  { value: "harbour", label: "Hafen" },
  { value: "lake", label: "See" },
  { value: "sea", label: "Meer" },
  { value: "canal", label: "Kanal" },
  { value: "ferry", label: "Fähre" },
  { value: "archipelago", label: "Schären" },
  { value: "island", label: "Insel" },
  { value: "water_view", label: "Wasserblick" },
  { value: "water_activity", label: "Aktivität am Wasser" },
];

export function createDefaultFilterState(): FilterState {
  return {
    query: "",
    minScores: {
      sunny: 0,
      rain: 0,
      cold: 0,
      history: 0,
      culture: 0,
      atmosphere: 0,
      walking: 0,
      water: 0,
      localness: 0,
      food: 0,
      photography: 0,
      children: 0,
      value: 0,
      spontaneity: 0,
      experienceDensity: 0,
      accessibility: 0,
      nature: 0,
      romance: 0,
      architecture: 0,
      timeEfficiency: 0,
    },
    travellerTypes: [],
    maxCostLevel: "any",
    maxPriceSek: null,
    maxDurationMinutes: null,
    maxTravelTimeMinutes: null,
    minIndoorPercentage: 0,
    seasons: [],
    timesOfDay: [],
    maxMobility: "any",
    bookingPolicy: "any",
    transportModes: [],
    neighbourhoodCharacters: [],
    waterRelationships: [],
    rainSafeOnly: false,
    childrenOnly: false,
    childAge: null,
    carFreeOnly: false,
    quietOnly: false,
    toiletsOnly: false,
    minStrollerScore: 0,
    maxPhysicalEffort: null,
    sortBy: "match",
    activePreset: null,
  };
}

function preset(mutator: (state: FilterState) => void): () => FilterState {
  return () => {
    const state = createDefaultFilterState();
    mutator(state);
    return state;
  };
}

export const presets: PresetDefinition[] = [
  {
    id: "sunny-outdoors",
    eyebrow: "Gutes Wetter",
    title: "Sonne & draußen",
    description: "Sonnenstark, atmosphärisch und mit viel Stockholm unter freiem Himmel.",
    accent: "sun",
    apply: preset((state) => {
      state.minScores.sunny = 5;
      state.minScores.atmosphere = 4;
      state.minScores.photography = 4;
      state.maxTravelTimeMinutes = 45;
      state.activePreset = "sunny-outdoors";
    }),
  },
  {
    id: "rain-history-flair",
    eyebrow: "Schlechtes Wetter",
    title: "Regen & Geschichte",
    description: "Wetterfest, historisch stark, atmosphärisch und höchstens mittleres Preisniveau.",
    accent: "rain",
    apply: preset((state) => {
      state.minScores.rain = 4;
      state.minScores.history = 4;
      state.minScores.atmosphere = 4;
      state.minIndoorPercentage = 75;
      state.maxCostLevel = "moderate";
      state.maxPriceSek = 250;
      state.rainSafeOnly = true;
      state.travellerTypes = ["history"];
      state.activePreset = "rain-history-flair";
    }),
  },
  {
    id: "walk-atmosphere",
    eyebrow: "Stadtgefühl",
    title: "Flair & Wege",
    description: "Zu Fuß entdecken, starke Atmosphäre, Fotomotive und viel Erlebnis auf kurzen Wegen.",
    accent: "walk",
    apply: preset((state) => {
      state.minScores.atmosphere = 4;
      state.minScores.walking = 4;
      state.minScores.photography = 4;
      state.minScores.experienceDensity = 4;
      state.maxTravelTimeMinutes = 30;
      state.activePreset = "walk-atmosphere";
    }),
  },
  {
    id: "family",
    eyebrow: "Mit Kindern",
    title: "Familientag",
    description: "Kindertauglich, gut erreichbar, ohne extremes Laufpensum und mit solidem Gegenwert.",
    accent: "family",
    apply: preset((state) => {
      state.minScores.children = 4;
      state.minScores.value = 3;
      state.childrenOnly = true;
      state.childAge = 8;
      state.maxTravelTimeMinutes = 30;
      state.maxPhysicalEffort = 4;
      state.travellerTypes = ["family"];
      state.activePreset = "family";
    }),
  },
  {
    id: "spontaneous-budget",
    eyebrow: "Ohne großen Plan",
    title: "Spontan & günstig",
    description: "Kurzfristig machbar, höchstens 100 SEK und gut für Reisende ohne feste Reservierungen.",
    accent: "budget",
    apply: preset((state) => {
      state.minScores.spontaneity = 4;
      state.minScores.value = 4;
      state.maxPriceSek = 100;
      state.maxCostLevel = "budget";
      state.bookingPolicy = "no_booking";
      state.carFreeOnly = true;
      state.activePreset = "spontaneous-budget";
    }),
  },
  {
    id: "water-photo",
    eyebrow: "Typisch Stockholm",
    title: "Wasser & Fotos",
    description: "Ufer, Inseln und starke Perspektiven – zentral erreichbar und besonders visuell.",
    accent: "water",
    apply: preset((state) => {
      state.minScores.water = 4;
      state.minScores.photography = 4;
      state.minScores.atmosphere = 4;
      state.maxTravelTimeMinutes = 30;
      state.travellerTypes = ["photography"];
      state.activePreset = "water-photo";
    }),
  },
];
